import DatePicker from '@app/src/components/Form/DatePicker'
import type { Option } from '@app/src/components/Form/Select/ControlledSelect'
import Select from '@app/src/components/Form/Select/ControlledSelect'
import { AUTOMATION_QUESTION_KEYS } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/CreateAutomationModal'
import { RequestAutomationType } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/RequestAutomationType'
import { getDateWithoutTimeFromString } from '@app/src/utils/helpersTs'
import { InfoOutlined } from '@mui/icons-material'
import { Alert, Box, Typography } from '@mui/material'
import React, { useEffect, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

const AutomationDeadlineOffset: React.FC = () => {
  const { control, watch, setValue, formState, trigger } = useFormContext()
  const { formatMessage, formatDate } = useIntl()
  const dayOffsetValues = [15, 30, 60]
  const automationType = watch(AUTOMATION_QUESTION_KEYS.AUTOMATION_TYPE) as RequestAutomationType

  const selectedDeadlineType = watch(AUTOMATION_QUESTION_KEYS.DEADLINE_OFFSET_TYPE)
  const selectedDeadlineValue = watch(AUTOMATION_QUESTION_KEYS.DEADLINE_OFFSET)
  const selectedCustomDate = watch(AUTOMATION_QUESTION_KEYS.CUSTOM_DUE_DATE)
  const dateFieldError = formState.errors[AUTOMATION_QUESTION_KEYS.CUSTOM_DUE_DATE]

  // Validation function for custom date - prevent past dates
  const validateDate = (value: string | Date | null) => {
    if (!value) return true

    // Parse date safely to avoid timezone issues
    const selectedDate = value instanceof Date ? value : getDateWithoutTimeFromString(value)
    const today = new Date()

    // Compare dates at midnight local time
    today.setHours(0, 0, 0, 0)
    selectedDate.setHours(0, 0, 0, 0)

    if (selectedDate < today) {
      return formatMessage({ id: 'form.validation.pastDateNotAllowed' })
    }
    return true
  }

  // On mount, sync DEADLINE_OFFSET with DEADLINE_OFFSET_TYPE for editing
  useEffect(() => {
    if (selectedDeadlineType !== undefined && selectedDeadlineValue === undefined) {
      setValue(AUTOMATION_QUESTION_KEYS.DEADLINE_OFFSET, selectedDeadlineType)
    }
  }, [])

  // Sync the two fields
  useEffect(() => {
    if (selectedDeadlineValue !== undefined) {
      setValue(AUTOMATION_QUESTION_KEYS.DEADLINE_OFFSET_TYPE, selectedDeadlineValue)
      // Clear custom date when switching to a preset option
      if (typeof selectedDeadlineValue === 'number') {
        setValue(AUTOMATION_QUESTION_KEYS.CUSTOM_DUE_DATE, undefined)
      }
    } else {
      // Clear both when DEADLINE_OFFSET is cleared (e.g., when Skip is clicked)
      setValue(AUTOMATION_QUESTION_KEYS.DEADLINE_OFFSET_TYPE, undefined)
      setValue(AUTOMATION_QUESTION_KEYS.CUSTOM_DUE_DATE, undefined)
    }
  }, [selectedDeadlineValue, setValue])

  const offsetOptions: Option<number>[] = dayOffsetValues.map(days => ({
    value: days,
    label: formatMessage(
      {
        id:
          automationType === RequestAutomationType.FOR_EVERY_NEW_PROVIDER
            ? 'form.createAutomation.deadlineOffset.optionTextForEveryNewProvider'
            : automationType === RequestAutomationType.FOR_PROVIDERS_WITH_NEW_ASSESSMENT_LEVEL
              ? 'form.createAutomation.deadlineOffset.optionTextForProvidersWithNewAssessmentLevel'
              : 'form.createAutomation.deadlineOffset.optionTextForProvidersInCategory',
      },
      { days: days },
    ),
  }))

  // Add the custom date option at the bottom
  const customDateOption: Option<number | 'custom'> = {
    value: 'custom',
    label: formatMessage({ id: 'form.createAutomation.deadlineOffset.customDateOption' }),
  }

  const allOptions: Option<number | 'custom'>[] = [...offsetOptions, customDateOption]

  // Format the selected date for display
  const formattedDate = useMemo(() => {
    if (!selectedCustomDate) return null
    const date = selectedCustomDate instanceof Date ? selectedCustomDate : new Date(selectedCustomDate)
    return formatDate(date, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }, [selectedCustomDate, formatDate])

  return (
    <>
      <Select
        name={AUTOMATION_QUESTION_KEYS.DEADLINE_OFFSET}
        control={control}
        fieldLabel={formatMessage({ id: 'form.createAutomation.deadlineOffset.dueDate' })}
        options={allOptions}
      />
      {selectedDeadlineType === 'custom' && (
        <Box mt={2}>
          <Typography variant="body2">
            {formatMessage({ id: 'form.createAutomation.deadlineOffset.fieldLabel' })}
          </Typography>
          <Controller
            name={AUTOMATION_QUESTION_KEYS.CUSTOM_DUE_DATE}
            control={control}
            rules={{
              validate: validateDate,
            }}
            render={({ onChange, value }) => (
              <DatePicker
                disablePast
                label=""
                value={value}
                onChange={newValue => {
                  onChange(newValue)
                  // Trigger validation after the value changes
                  setTimeout(() => trigger(AUTOMATION_QUESTION_KEYS.CUSTOM_DUE_DATE), 0)
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: Boolean(dateFieldError),
                    helperText: dateFieldError?.message as string,
                  },
                }}
              />
            )}
          />
          {formattedDate && (
            <Alert severity="info" icon={<InfoOutlined />} sx={{ mt: 2 }}>
              {formatMessage({ id: 'form.createAutomation.deadlineOffset.customDateWarning' })}
            </Alert>
          )}
        </Box>
      )}
    </>
  )
}
export default AutomationDeadlineOffset
